import ModalLayout, {
  ModalLayoutProps,
} from "components/modal/modal-layout/ModalLayout";
import { DOB_FORMAT } from "components/patient-editor/constants/dobFormat";
import { useClientsContext } from "contexts/ClientsContext";
import messages from "messages";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Button, Dropdown, FormControl } from "react-bootstrap";
import getClientDisplayName from "utils/getClientDisplayName";
import searchClientsByName from "utils/searchClientsByName";
import styles from "./ContactSelector.module.scss";

const PATIENT_INPUT_ID = "patient-input";

type Props = {
  isForFormSharing?: boolean;
  onApply: (arg: PatientChartContact) => void;
} & Pick<ModalLayoutProps, "show" | "onHide">;

/**
 * renders UI modal to select contact
 */
const ContactSelector = ({
  isForFormSharing,
  onApply,
  show,
  onHide,
}: Props) => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<PatientChartContact | undefined>(
    undefined,
  );

  const { individuals, couples, initializeClients } = useClientsContext();

  const clients = [...individuals, ...couples];

  const clientsFiltered = searchClientsByName(clients, search)
    .sort((a, b) =>
      getClientDisplayName(a) < getClientDisplayName(b) ? -1 : 1,
    )
    ?.filter((client) => client.patient.client_status === "patient");

  useEffect(() => {
    initializeClients();
  }, [initializeClients]);

  return (
    <ModalLayout
      show={show}
      scrollable={false}
      onHide={onHide}
      showHeaderDivider={false}
      Header={isForFormSharing ? "Share document" : messages.searchPatient}
      Footer={
        <>
          <Button
            className={styles["contact-selector__button"]}
            variant={"outline-primary"}
            onClick={onHide}
          >
            {messages.cancel}
          </Button>
          <Button
            className={styles["contact-selector__button"]}
            variant={"primary"}
            disabled={!selected}
            onClick={() => {
              return selected && onApply(selected);
            }}
          >
            {isForFormSharing ? "Share" : messages.select}
          </Button>
        </>
      }
      headerClass={styles["contact-selector__header"]}
      bodyClass={styles["contact-selector__body"]}
      footerClass={styles["contact-selector__footer"]}
      contentClassName={styles["contact-selector__content"]}
      backdropClassName={styles["contact-selector__backdrop"]}
    >
      <label
        htmlFor={PATIENT_INPUT_ID}
        className={styles["contact-selector__label"]}
      >
        {isForFormSharing
          ? "Search and select a patient from existing patients and contacts on file. The document will be share to the client's portal."
          : messages.searchAndSelect}
      </label>

      <Dropdown className={styles["contact-selector__dropdown"]}>
        <Dropdown.Toggle
          as={FormControl}
          placeholder="Search for a client"
          id="patient-input"
          value={search}
          onChange={(e) => {
            setSelected(undefined);
            setSearch((e.target as HTMLInputElement).value);
          }}
        />

        {search !== "" && (
          <Dropdown.Menu className={styles["contact-selector__dropdown-menu"]}>
            {clientsFiltered.length === 0 ? (
              <Dropdown.Item disabled>No clients found</Dropdown.Item>
            ) : (
              clientsFiltered.map((client) => (
                <Dropdown.Item
                  key={client.patient.orchid_user_id}
                  onClick={() => {
                    setSelected(client.patient);
                    setSearch(getClientDisplayName(client));
                  }}
                >
                  <div className={styles["contact-selector__dropdown-item"]}>
                    {getClientDisplayName(client)}
                    <span className={styles["contact-selector__date"]}>
                      {client.patient.dob
                        ? moment(client.patient.dob).format(DOB_FORMAT)
                        : ""}
                    </span>
                  </div>
                </Dropdown.Item>
              ))
            )}
          </Dropdown.Menu>
        )}
      </Dropdown>
    </ModalLayout>
  );
};

export default ContactSelector;
